import type { ASTPath, Collection, ImportSpecifier, JSCodeshift } from 'jscodeshift';

/**
 * Checks if a specific import exists in the given root collection and provides
 * a method to remove it if found.
 */
function hasImport(
  root: Collection,
  sourceValue: string,
  importName: string,
  j: JSCodeshift,
): {
  exists: boolean;
  remove: () => void;
  aliases?: Collection<ImportSpecifier>;
  resolvedName: string;
  conflictingImports: ImportSpecifier[];
} {
  const importDeclarations = root.find(j.ImportDeclaration, {
    source: { value: sourceValue },
  });

  /**
   * Finds all ImportSpecifier nodes that expose `importName` but
   * from a different source than `sourceValue`.
   */
  const conflictingImports = ((): ImportSpecifier[] => {
    const result: ImportSpecifier[] = [];
    root
      .find(j.ImportDeclaration)
      .filter((path) => path.node.source.value !== sourceValue)
      .forEach((path) => {
        for (const specifier of path.node.specifiers ?? []) {
          if (
            specifier.type === 'ImportSpecifier' &&
            specifier.imported.name === importName &&
            specifier.local?.name === importName
          ) {
            result.push(specifier);
          }
        }
      });
    return result;
  })();

  if (importDeclarations.size() === 0) {
    return {
      exists: false,
      remove: () => {},
      resolvedName: importName,
      conflictingImports,
    };
  }

  const namedImport = importDeclarations.find(j.ImportSpecifier, {
    imported: { name: importName },
  });

  const defaultImport = importDeclarations.find(j.ImportDefaultSpecifier, {
    local: { name: importName },
  });

  const aliasImport = importDeclarations.find(j.ImportSpecifier).filter((path) => {
    return (
      path.node.imported.name === importName && path.node.imported.name !== path.node.local?.name
    );
  });

  const exists = namedImport.size() > 0 || defaultImport.size() > 0;

  const resolveName = (): string => {
    if (aliasImport.size() > 0) {
      const importPath = aliasImport.get(0) as ASTPath<ImportSpecifier>;
      const localName = importPath.node.local?.name;

      if (typeof localName === 'string') {
        return localName;
      }

      if (
        localName &&
        typeof localName === 'object' &&
        'name' in localName &&
        typeof localName.name === 'string'
      ) {
        return localName.name;
      }

      return importName;
    }

    return importName;
  };

  const remove = () => {
    importDeclarations.forEach((path) => {
      const filteredSpecifiers =
        path.node.specifiers?.filter((specifier) => {
          if (specifier.type === 'ImportSpecifier' && specifier.imported.name === importName) {
            return false;
          }
          if (specifier.type === 'ImportDefaultSpecifier' && specifier.local?.name === importName) {
            return false;
          }
          return true;
        }) ?? [];

      if (filteredSpecifiers.length === 0) {
        path.prune();
      } else {
        j(path).replaceWith(
          j.importDeclaration(filteredSpecifiers, path.node.source, path.node.importKind),
        );
      }
    });
  };

  return {
    exists,
    remove,
    aliases: aliasImport,
    resolvedName: resolveName(),
    conflictingImports,
  };
}

export default hasImport;
